import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { CourseAccessGuard } from '../components/CourseAccessGuard';
import { DashboardLayout } from '../components/DashboardLayout';
import { useLiveSessions } from '../hooks/useLiveSessions';
import { useHomework } from '../hooks/useHomework';
import { QuestionFormData } from '../types/liveSessions';
import { HomeworkService } from '../services/homeworkService';
import { LiveSessionVideo } from '../components/LiveSessionVideo';

export const LiveSessions: React.FC = () => {
  const { user } = useAuth();
  const [selectedSession, setSelectedSession] = useState<number | null>(null);
  const [newQuestion, setNewQuestion] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<{ show: boolean; questionId: string | null }>({
    show: false,
    questionId: null
  });
  const [homeworkDeleteConfirm, setHomeworkDeleteConfirm] = useState<{ show: boolean; fileName: string | null }>({
    show: false,
    fileName: null
  });
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const {
    questions,
    availableSessions,
    isSubmitting,
    error,
    submitQuestion,
    upvoteQuestion,
    deleteQuestion,
    clearError,
  } = useLiveSessions(selectedSession);

  const {
    homeworkFiles,
    isUploading,
    isLoading: isLoadingHomework,
    error: homeworkError,
    uploadHomework,
    deleteHomework,
    clearError: clearHomeworkError,
  } = useHomework(selectedSession);

  useEffect(() => {
    if (availableSessions.length > 0 && !selectedSession) {
      setSelectedSession(availableSessions[0].id);
    }
  }, [availableSessions, selectedSession]);

  const handleSubmitQuestion = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newQuestion.trim()) return;

    const questionData: QuestionFormData = {
      question: newQuestion.trim(),
    };

    const success = await submitQuestion(questionData);
    if (success) {
      setNewQuestion('');
    }
  };

  const handleDeleteClick = (questionId: string) => {
    setDeleteConfirm({ show: true, questionId });
  };

  const handleDeleteConfirm = async () => {
    if (deleteConfirm.questionId) {
      await deleteQuestion(deleteConfirm.questionId);
      setDeleteConfirm({ show: false, questionId: null });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirm({ show: false, questionId: null });
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const success = await uploadHomework(file);
    if (success && fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleHomeworkDeleteClick = (fileName: string) => {
    setHomeworkDeleteConfirm({ show: true, fileName });
  };

  const handleHomeworkDeleteConfirm = async () => {
    if (homeworkDeleteConfirm.fileName) {
      await deleteHomework(homeworkDeleteConfirm.fileName);
      setHomeworkDeleteConfirm({ show: false, fileName: null });
    }
  };

  const handleHomeworkDeleteCancel = () => {
    setHomeworkDeleteConfirm({ show: false, fileName: null });
  };

  const selectedSessionData = availableSessions.find(session => session.id === selectedSession);

  return (
    <CourseAccessGuard>
      <DashboardLayout>
        <div className="py-6 bg-[#FCFAF7] min-h-screen">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
            <h1 className="text-2xl font-semibold text-[#203D32] font-alegreya mb-6">Live Sessions</h1>
            
            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-800 text-sm">{error}</p>
              </div>
            )}

            {homeworkError && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-800 text-sm">{homeworkError}</p>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Session List */}
              <div className="lg:col-span-1">
                <div className="bg-white shadow rounded-lg p-6">
                  <h2 className="text-lg font-semibold text-[#203D32] mb-4">Available Sessions</h2>
                  <div className="space-y-3">
                    {availableSessions.map((session) => (
                      <button
                        key={session.id}
                        onClick={() => setSelectedSession(session.id)}
                        className={`w-full text-left p-4 rounded-lg border transition-colors duration-200 ${
                          selectedSession === session.id
                            ? 'border-[#203D32] bg-[#F0F7F1]'
                            : 'border-gray-200 hover:border-[#203D32] hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h3 className="font-medium text-[#203D32]">{session.title}</h3>
                            <p className="text-sm text-gray-600 mt-1">{session.date} at {session.time}</p>
                            {session.description && (
                              <p className="text-sm text-gray-500 mt-1">{session.description}</p>
                            )}
                          </div>
                          {session.hasRecording && (
                            <div className="flex items-center space-x-1 ml-2">
                              <svg className="h-4 w-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                              <span className="text-xs text-green-600 font-medium">Recording</span>
                            </div>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Q&A Section */}
              <div className="lg:col-span-2">
                <div className="bg-white shadow rounded-lg p-6">
                  {selectedSessionData ? (
                    <>
                      <div className="mb-6">
                        <h2 className="text-xl font-semibold text-[#203D32] mb-2">
                          {selectedSessionData.title}
                        </h2>
                        <p className="text-gray-600">
                          {selectedSessionData.date} at {selectedSessionData.time}
                        </p>
                        {selectedSessionData.description && (
                          <p className="text-gray-600 mt-2">{selectedSessionData.description}</p>
                        )}
                      </div>

                      {/* Session Recording Section */}
                      {selectedSessionData.hasRecording && selectedSessionData.videoId && (
                        <div className="mb-8">
                          <LiveSessionVideo
                            videoId={selectedSessionData.videoId}
                            title={selectedSessionData.videoTitle || `${selectedSessionData.title} - Recording`}
                            description={selectedSessionData.videoDescription}
                          />
                        </div>
                      )}

                      {/* No Recording Available Message */}
                      {!selectedSessionData.hasRecording && (
                        <div className="mb-8 p-6 bg-blue-50 border border-blue-200 rounded-lg">
                          <div className="flex items-center space-x-3">
                            <svg className="h-6 w-6 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <div>
                              <h3 className="text-lg font-medium text-blue-800">Recording Not Available</h3>
                              <p className="text-blue-600 text-sm mt-1">
                                This session hasn't been recorded yet or the recording is being processed.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Homework Upload Section */}
                      <div className="mb-8 p-4 bg-[#FCFAF7] rounded-lg border border-[#E5EFE7]">
                        <h3 className="text-lg font-semibold text-[#203D32] mb-4">Upload Homework</h3>
                        
                        {/* Upload Form */}
                        <div className="mb-6">
                          <input
                            ref={fileInputRef}
                            type="file"
                            onChange={handleFileUpload}
                            accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.zip,.rar"
                            className="hidden"
                            disabled={isUploading}
                          />
                          <button
                            onClick={() => fileInputRef.current?.click()}
                            disabled={isUploading}
                            className="px-4 py-2 bg-[#203D32] text-white rounded-md hover:bg-[#1a2f28] disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                          >
                            {isUploading ? 'Uploading...' : 'Choose File'}
                          </button>
                        </div>

                        {/* Homework Files List */}
                        <div>
                          <h4 className="text-md font-medium text-[#203D32] mb-3">Your Homework Files</h4>
                          {isLoadingHomework ? (
                            <p className="text-gray-500">Loading homework files...</p>
                          ) : homeworkFiles.length === 0 ? (
                            <p className="text-gray-500">No homework files uploaded yet.</p>
                          ) : (
                            <div className="space-y-3">
                              {homeworkFiles.map((file) => {
                                const isOwner = user && file.uploadedBy === user.email;
                                return (
                                  <div key={file.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
                                    <div className="flex items-center space-x-3">
                                      <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                      </svg>
                                      <div>
                                        <p className="text-sm font-medium text-[#203D32]">{file.name}</p>
                                        <p className="text-xs text-gray-500">
                                          {HomeworkService.formatFileSize(file.size)} • {file.uploadedAt.toLocaleDateString()}
                                        </p>
                                      </div>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                      <a
                                        href={file.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-blue-600 hover:text-blue-800 text-sm"
                                      >
                                        Download
                                      </a>
                                      {isOwner && (
                                        <button
                                          onClick={() => handleHomeworkDeleteClick(file.id)}
                                          className="text-red-600 hover:text-red-800 text-sm"
                                        >
                                          Delete
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Ask Question Form */}
                      <div className="mb-8 p-4 bg-[#FCFAF7] rounded-lg border border-[#E5EFE7]">
                        <h3 className="text-lg font-semibold text-[#203D32] mb-4">Ask a Question</h3>
                        <form onSubmit={handleSubmitQuestion}>
                          <div className="mb-4">
                            <textarea
                              value={newQuestion}
                              onChange={(e) => setNewQuestion(e.target.value)}
                              placeholder="Type your question here..."
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#203D32] focus:border-transparent"
                              rows={3}
                              disabled={isSubmitting}
                            />
                          </div>
                          <button
                            type="submit"
                            disabled={!newQuestion.trim() || isSubmitting}
                            className="px-4 py-2 bg-[#203D32] text-white rounded-md hover:bg-[#1a2f28] disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                          >
                            {isSubmitting ? 'Submitting...' : 'Submit Question'}
                          </button>
                        </form>
                      </div>

                      {/* Questions List */}
                      <div>
                        <h3 className="text-lg font-semibold text-[#203D32] mb-4">
                          Questions ({questions.length})
                        </h3>
                        <div className="space-y-4">
                          {questions.length === 0 ? (
                            <p className="text-gray-500 text-center py-8">No questions yet. Be the first to ask!</p>
                          ) : (
                            questions.map((question) => {
                              const hasUpvoted = user && question.upvotedBy.includes(user.id);
                              const isOwner = user && question.userId === user.id;

                              return (
                                <div key={question.id} className="border border-gray-200 rounded-lg p-4">
                                  <div className="flex justify-between items-start">
                                    <div className="flex-1">
                                      <p className="text-[#203D32] font-medium mb-2">{question.question}</p>
                                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                                        <span>By {question.userName}</span>
                                      </div>
                                    </div>
                                    <div className="flex items-center space-x-2 ml-4">
                                      <button
                                        onClick={() => upvoteQuestion(question.id)}
                                        className={`flex items-center space-x-1 px-3 py-1 rounded-full text-sm font-medium transition-colors duration-200 ${
                                          hasUpvoted
                                            ? 'bg-[#203D32] text-white'
                                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                        }`}
                                      >
                                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
                                        </svg>
                                        <span>{question.votes}</span>
                                      </button>
                                      {isOwner && (
                                        <button
                                          onClick={() => handleDeleteClick(question.id)}
                                          className="text-red-600 hover:text-red-800 text-sm"
                                        >
                                          Delete
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })
                          )}
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-gray-500">No sessions available</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        {deleteConfirm.show && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3 text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                  <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mt-4">Delete Question</h3>
                <div className="mt-2 px-7 py-3">
                  <p className="text-sm text-gray-500">
                    Are you sure you want to delete this question? This action cannot be undone.
                  </p>
                </div>
                <div className="items-center px-4 py-3">
                  <button
                    onClick={handleDeleteConfirm}
                    className="px-4 py-2 bg-red-500 text-white text-base font-medium rounded-md w-24 mr-2 hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-300"
                  >
                    Delete
                  </button>
                  <button
                    onClick={handleDeleteCancel}
                    className="px-4 py-2 bg-gray-500 text-white text-base font-medium rounded-md w-24 hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-300"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Homework Delete Confirmation Modal */}
        {homeworkDeleteConfirm.show && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3 text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                  <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mt-4">Delete Homework File</h3>
                <div className="mt-2 px-7 py-3">
                  <p className="text-sm text-gray-500">
                    Are you sure you want to delete this homework file? This action cannot be undone.
                  </p>
                </div>
                <div className="items-center px-4 py-3">
                  <button
                    onClick={handleHomeworkDeleteConfirm}
                    className="px-4 py-2 bg-red-500 text-white text-base font-medium rounded-md w-24 mr-2 hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-300"
                  >
                    Delete
                  </button>
                  <button
                    onClick={handleHomeworkDeleteCancel}
                    className="px-4 py-2 bg-gray-500 text-white text-base font-medium rounded-md w-24 hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-300"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </DashboardLayout>
    </CourseAccessGuard>
  );
}; 